import {ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output} from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { RequestTemplateService } from 'src/app/services/request-template/request-template.service';
import { AlertBannerService, AlertType } from 'src/app/lib/alert-banner';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import moment from 'moment';

@Component({
  selector: 'app-select-template-modal',
  templateUrl: './select-template-modal.component.pug',
  styleUrls: ['./select-template-modal.component.scss'],
  standalone: false
})
export class SelectTemplateModalComponent implements OnInit, OnDestroy {
  public templateSelect: EventEmitter<any> = new EventEmitter();
  
  templates: any[] = [];
  loading = false;
  templatesLoaded = false;
  currentPage = 1;
  rowsPerView = 20;
  total = 0;
  selectedTemplate: any = null;
  searchTerm = '';
  sortString = '-last_used';
  sortLabel = $localize`Last used`;
  filterTags: string[] = [];
  noSearchTitle = $localize`Sorry, we couldn’t find any results`
  noSearchDescription = $localize`Try another search term or adjust the filters.`
  searchImg = "https://assets.xref.com/omni/shared/tmp_console_no_vendors.svg"
  searchOptions = [
      { label: $localize`Package`, hint: $localize`Package title` },
  ];
  searchedData: any = [];
  searchTags: any = [];
  searchOutput: any = undefined;
  
  private destroy$ = new Subject<void>();

  sortOptions = [
      {
        key: $localize`Last used`,
        value: [
          { label: $localize`Newest`, sortString: '-last_used' },
          { label: $localize`Oldest`, sortString: 'last_used' },
        ],
      },
      {
        key: 'group-end',
        value: [],
      },
      {
        key: $localize`Times used`,
        value: [
          { label: $localize`Highest`, sortString: '-usage' },
          { label: $localize`Lowest`, sortString: 'usage' },
        ],
      },
  ];

  constructor(
    public bsModalRef: BsModalRef,
    private templateService: RequestTemplateService,
    private alertList: AlertBannerService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.sortLabel = `${this.sortOptions[0].key} (${this.sortOptions[0].value[0].label})`;
    this.getAndProcessTemplatesList();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getAndProcessTemplatesList(): void {
    this.loading = true;
    this.templatesLoaded = false;

    this.templateService.listTemplates(this.rowsPerView, this.currentPage,this.searchTerm, this.sortString, 'active').pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        requestAnimationFrame(() => {
          this.processTemplates(response['templates'] || response['data'] || []);
          this.total = response['total'] || response['count'] || 0;
          this.templatesLoaded = true;
          this.loading = false;
          this.cdr.detectChanges();
        });
      },
      error: (error) => {
        console.error('Error loading templates:', error);
        this.alertList.append([{
          type: AlertType.DANGER,
          message: $localize`Unable to load templates. Please try again.`,
          keepOnRouteChange: false
        }]);
        requestAnimationFrame(() => {
          this.templatesLoaded = true;
          this.loading = false;
          this.cdr.detectChanges();
        });
      }
    });
  }

  private processTemplates(templates: any[]): void {
    this.templates = templates.map(template => {
      const backgroundConfig = template.config?.find((c: any) => c.type === 'background');
      const backgroundChecks = backgroundConfig?.partner_checks
        ?.map((check: any) => check.name)
        .join(', ') || '';
      
      const referenceConfig = template.config?.find((c: any) => c.type === 'reference');
      const requestValue = referenceConfig?.package_name || '';
      
      const lastUsed = this.formatDate(template.last_used);
      const timesUsed = template.usage_count != null ? String(template.usage_count) : '0';
      
      return {
        ...template,
        packageName: template.name || '',
        lastUsed,
        lastUsedLabel: $localize`Last used`,
        requestLabel: $localize`Request`,
        requestValue,
        backgroundChecks,
        timesUsed,
        checkLabel: $localize`Background checks`,
        timeLabel: $localize`Times used`
      };
    });
  }

  private formatDate(date: string | null | undefined): string | null {
    if (!date) {
      return null;
    }
    return moment(date, 'YYYY-MM-DD').format('D MMM YYYY');
  }

  onTemplateSelect(template: any): void {
    this.selectedTemplate = template;
  }

  onSearchChange(searchTerm: string): void {
    this.searchTerm = searchTerm;
    // TODO: Implement search filtering
    // For now, just reset to first page
    this.currentPage = 1;
    this.getAndProcessTemplatesList();
  }

  onSortChange(event: any): void {
    this.sortString = event.sortString;
    this.sortLabel = `${event.key} (${event.label})`;
    // Reset to first page when sorting changes
    this.currentPage = 1;
    this.getAndProcessTemplatesList();
  }


  onPageChange(page: number): void {
    if (this.currentPage !== page) {
      this.currentPage = page;
      this.getAndProcessTemplatesList();
    }
  }

  onPageSizeChange(size: number): void {
    this.rowsPerView = size;
    this.currentPage = 1;
    this.getAndProcessTemplatesList();
  }

  onSelectTemplate(): void {
    if (this.selectedTemplate) {
      this.templateSelect.emit({ template: this.selectedTemplate });
      this.bsModalRef.hide();
    }
  }

  onCancel(): void {
    this.bsModalRef.hide();
  }

  getTimesUsedText(timesUsed: number | string | null | undefined): string {
    if (timesUsed === 0 || timesUsed === null || timesUsed === undefined || timesUsed === '0') {
      return '0';
    }
    return String(timesUsed);
  }

  clearTags() {
    this.searchedData = [];
    this.currentPage = 1;
    this.searchTerm = '';
    this.getAndProcessTemplatesList();
  }

  textValueChanged(event: any) {
    this.searchedData[0] = {key: event['type'].toLowerCase(), value: event['search']};
    this.searchTerm = event['search'];
    this.currentPage = 1;
    this.getAndProcessTemplatesList();
  }
  createSearchOption(): { key: string; value: string }[] {
    return this.searchTags.map(item => ({
      key: item.value === '' ? 'all' : item.value,
      value: item.term
    }));
  }

  onRemoveSearchTag(searchOutput){
    this.searchTags = searchOutput
    this.searchOutput = this.createSearchOption()
  }

  checkOverflow(element: any): boolean {
    if (!element) {
      return false;
    }
    const el = element.nativeElement || element;
    if (!el || typeof el.offsetWidth === 'undefined' || typeof el.scrollWidth === 'undefined') {
      return false;
    }
    return el.offsetWidth < el.scrollWidth;
  }


}
